<template>
  <b-row no-gutters>
    <b-col>
      <div v-if="!!assetsPerCompanyFromSummary && !loading">
        <div class="asset-list__scroll-table">
          <s-table
            class="table-wrapper"
            caption-top
            v-for="item in assetsPerCompanyFromSummary"
            :responsive="true"
            :fields="fields"
            :items="getAssetsByCompanyId(item.companyId)"
            :key="item.companyId"
          >
            <template #table-caption>
              <div class="caption">
                <h2 class="d-inline-block font-weight-bold">{{ item.companyName }}</h2>
                <span class="badge info-badge">
                  {{ getNumberOfAssetsText(item.companyId) }}
                </span>
              </div>
            </template>
            <template #cell(regionalName)="{ item }">
              {{ item.regionalName | emptyValueFallbackFilter }}
            </template>
            <template #cell(installationName)="{ item }">
              {{ item.installationName | emptyValueFallbackFilter }}
            </template>
            <template #cell(assetType)="{ item }">
              {{ item.assetType | emptyValueFallbackFilter }}
            </template>
            <template #cell(serialNumber)="{ item }">
              {{ item.serialNumber | emptyValueFallbackFilter }}
            </template>
            <template #cell(assetName)="{ item }">
              {{ item.assetName | emptyValueFallbackFilter }}
            </template>
            <template #cell(actions)="data">
              <div class="asset-list__buttons">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="goToAsset(data.item)"
                  @mousedown.middle="goToAsset(data.item)"
                >
                  {{ $t('asset-list.access') }}
                </b-button>
                <b-dropdown
                  class="asset-list__button"
                  variant="link"
                  offset="-15"
                  right
                  no-caret
                >
                  <template slot="button-content" v-if="assetHasFunctionalities(data.item)">
                    <span class="material-icons text-gray">more_vert</span>
                  </template>
                  <template>
                    <b-dropdown-item
                      target="_blank"
                      :href="goToAssetInformation(data.item)"
                    >
                    <span class="material-icons">
                      info
                    </span>
                      <span class="asset-list__dropdown-text-item">
                      {{ $t('asset-list.asset-info') }}
                    </span>
                    </b-dropdown-item>
                  </template>
                </b-dropdown>
              </div>
            </template>
          </s-table>
        </div>
      </div>
      <s-loader
        v-else-if="loading"
        class="py-5"
        size="md"
      />
      <s-empty-list v-else/>
    </b-col>
  </b-row>
</template>

<script>
import emptyValueFallbackFilter from '@/filters/emptyValueFallbackFilter';

export default {
  props: {
    items: {
      type: Array,
      required: true,
    },
    summary: {
      type: Object,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    assetsPerCompanyFromSummary() {
      return (this.summary || {}).assetsPerCompany;
    },
    fields() {
      return [
        {
          key: 'regionalName',
          label: this.$t('asset-list.regional'),
          class: 'asset-list__cell-regional-name',
        },
        {
          key: 'installationName',
          label: this.$t('asset-list.installation'),
          class: 'asset-list__cell-installation-name',
        },
        {
          key: 'assetType',
          label: this.$t('asset-list.asset-type'),
          class: 'asset-list__cell-asset-type',
        },
        {
          key: 'serialNumber',
          label: this.$t('asset-list.serial-number'),
          class: 'asset-list__cell-serial-number',
        },
        {
          key: 'assetName',
          label: this.$t('asset-list.asset-name'),
          class: 'asset-list__cell-asset-name',
        },
        {
          key: 'actions',
          label: this.$t('asset-list.actions'),
          class: 'asset-list__cell-actions',
        },
      ];
    },
  },
  filters: {
    emptyValueFallbackFilter,
  },
  methods: {
    goToAssetInformation(item) {
      const routeData = this.$router.resolve({
        name: 'monitoring.asset-information.registration-data',
        params: {
          installationId: item.installationId,
          assetId: item.assetId,
        },
      });
      return routeData.href;
    },
    goToAsset(item) {
      const link = `/#/instalacao/${item.installationId}/ativo/${item.assetId}/`;
      window.open(link, '_blank');
    },
    getAssetsByCompanyId(companyId) {
      return (this.items || []).filter(item => item.companyId === companyId);
    },
    getCompanyFromSummary(companyId) {
      return this.assetsPerCompanyFromSummary.find(item => item.companyId === companyId);
    },
    getNumberOfAssetsOfCompany(companyId) {
      return this.getCompanyFromSummary(companyId).amountAssets;
    },
    getNumberOfAssets(companyId) {
      return this.getAssetsByCompanyId(companyId).length;
    },
    assetHasFunctionalities(assetList) {
      return (assetList.assetFeatures.length > 0);
    },
    getNumberOfAssetsText(companyId) {
      const showingNumber = this.getNumberOfAssets(companyId);
      const totalNumber = this.getNumberOfAssetsOfCompany(companyId);
      return this.$t('asset-list.showing-n-of-n-assets', {
        showingNumber,
        totalNumber,
      });
    },
  },
};
</script>

<style lang="sass" scoped>
.asset-list
  &__dropdown-text-item
    margin-left: 0.5rem

  &__scroll-table
    max-height: calc(100vh - 14rem)
    overflow: auto
    margin-bottom: 1rem

  &__buttons
    min-width: 8rem

  &__button
    ::v-deep
      .btn.dropdown-toggle
        padding: 0.2rem 0.07rem
        span
          vertical-align: middle

  ::v-deep &__cell
    &-regional-name
      width: 18%
    &-installation-name
      width: 18%
    &-asset-type
      width: 18%
    &-serial-number
      width: 18%
    &-asset-name
      width: 18%
    &-actions
      width: 10%
    &-regional-name,
    &-installation-name,
    &-asset-type,
    &-serial-number,
    &-asset-name,
    &-actions
      word-break: break-word

.caption
  & h2
    font-size: 1.3rem
    margin-right: 10px
    color: var(--max-theme-opposite-color)

  & span
    font-size: 1rem
    background-color: rgba(0, 0, 0, 0.1)
    border-radius: 50px
    color: var(--max-theme-opposite-color)
    &.badge
      color: var(--color-black-87) !important
      background-color: var(--color-oposite-faded-background) !important

::v-deep
  table
    &.table
      background-color: transparent

      thead th
        background-color: transparent
        border-bottom: 1px solid #dee2e6
        font-size: .9rem
        color: var(--max-theme-opposite-color)

      & th,
      & td
        padding: 1rem .75rem

    & tbody tr:nth-of-type(even),
    & tbody tr:nth-of-type(odd),
    &.table-striped tbody tr:nth-of-type(even),
    &.table-striped tbody tr:nth-of-type(odd)
      background-color: transparent
      border-bottom: 1px solid #dee2e6

    &.table-hover
      tbody
        tr:hover
          background-color: rgba(0, 0, 0, 0.06)

  .dropdown-item
    display: flex

@media not all and (min-resolution: .001dpcm)
  @supports (-webkit-appearance: none)
    .asset-list
      &__scroll-table
        max-height: calc(100vh - 18rem)

</style>
